import { cn } from '@/utils/cn';

/**
 * The project's icon set, drawn inline.
 *
 * No icon package: the admin console needs about twenty glyphs, and a
 * dependency would ship several hundred. These inherit `currentColor` and the
 * font size around them, so an icon is coloured by the same tokens as the text
 * it sits beside -- which a sprite sheet or an icon font cannot do.
 *
 * All paths are drawn on a 24x24 grid with a 1.5 stroke, so they stay optically
 * consistent when mixed at different sizes.
 */

const PATHS = {
  // -- shell navigation ----------------------------------------------------
  // Added for the student and company rails, which listed their destinations
  // as bare text before the shell gave every row an icon.
  home: 'M4 11.2 12 4l8 7.2V19a1 1 0 0 1-1 1h-4.5v-5.5h-5V20H5a1 1 0 0 1-1-1v-7.8Z',
  search: 'M11 4.5a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13Zm4.8 11.3L20 20',
  bookmark: 'M7 3.75h10a1 1 0 0 1 1 1v15.5l-6-4.2-6 4.2V4.75a1 1 0 0 1 1-1Z',
  calendar:
    'M4.75 7.5a1 1 0 0 1 1-1h12.5a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1H5.75a1 1 0 0 1-1-1v-11Zm0 3.75h14.5M8.75 4.5v4m6.5-4v4',
  document:
    'M6.75 3.75h6.5l5 5v11.5a1 1 0 0 1-1 1h-10.5a1 1 0 0 1-1-1V4.75a1 1 0 0 1 1-1Zm6.5 0v5h5',
  // Concentric rings: a goal, for the career planner.
  target:
    'M12 3.75a8.25 8.25 0 1 0 0 16.5 8.25 8.25 0 0 0 0-16.5Zm0 4.5a3.75 3.75 0 1 0 0 7.5 3.75 3.75 0 0 0 0-7.5Z',
  settings:
    'M12 15.25a3.25 3.25 0 1 0 0-6.5 3.25 3.25 0 0 0 0 6.5ZM19.4 15a1.4 1.4 0 0 0 .28 1.55l.05.05a1.7 1.7 0 1 1-2.4 2.4l-.05-.05a1.4 1.4 0 0 0-1.55-.28 1.4 1.4 0 0 0-.85 1.28v.14a1.7 1.7 0 0 1-3.4 0v-.07a1.4 1.4 0 0 0-.92-1.28 1.4 1.4 0 0 0-1.55.28l-.05.05a1.7 1.7 0 1 1-2.4-2.4l.05-.05a1.4 1.4 0 0 0 .28-1.55 1.4 1.4 0 0 0-1.28-.85H5.4a1.7 1.7 0 1 1 0-3.4h.07a1.4 1.4 0 0 0 1.28-.92 1.4 1.4 0 0 0-.28-1.55l-.05-.05a1.7 1.7 0 1 1 2.4-2.4l.05.05a1.4 1.4 0 0 0 1.55.28h.07a1.4 1.4 0 0 0 .85-1.28V4.4a1.7 1.7 0 0 1 3.4 0v.07a1.4 1.4 0 0 0 .85 1.28 1.4 1.4 0 0 0 1.55-.28l.05-.05a1.7 1.7 0 1 1 2.4 2.4l-.05.05a1.4 1.4 0 0 0-.28 1.55v.07a1.4 1.4 0 0 0 1.28.85h.14a1.7 1.7 0 0 1 0 3.4h-.07a1.4 1.4 0 0 0-1.28.85Z',
  logout:
    'M15 5.25V4.5a1 1 0 0 0-1-1H5.5a1 1 0 0 0-1 1v15a1 1 0 0 0 1 1H14a1 1 0 0 0 1-1v-.75M10.5 12h9.25m-3-3 3 3-3 3',
  // The filled `clock` above is a disc whose hands do not subtract under the
  // default nonzero fill rule, so at 14px it reads as a status dot. This is
  // the outline drawing, for chips where the glyph has to say "time".
  clockOutline: 'M12 3.75a8.25 8.25 0 1 0 0 16.5 8.25 8.25 0 0 0 0-16.5ZM12 7.75V12.4l3.2 1.9',
  bell: 'M6.25 9.75a5.75 5.75 0 0 1 11.5 0c0 3.9 1.45 5.3 1.95 5.85H4.3c.5-.55 1.95-1.95 1.95-5.85ZM10 18.75a2 2 0 0 0 4 0',
  // -- navigation ----------------------------------------------------------
  overview: 'M4 13h6V4H4v9Zm0 7h6v-5H4v5Zm10 0h6v-9h-6v9Zm0-16v5h6V4h-6Z',
  verification:
    'M12 3 5 6v5.5c0 4.3 2.9 8.3 7 9.5 4.1-1.2 7-5.2 7-9.5V6l-7-3Zm-1.2 12L8 12.2l1.1-1.1 1.7 1.7 4.1-4.1L16 9.8 10.8 15Z',
  internship:
    'M9 6V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v1h3a2 2 0 0 1 2 2v3H3V8a2 2 0 0 1 2-2h4Zm2 0h2V5h-2v1ZM3 13h6v2h6v-2h6v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-5Z',
  users:
    'M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm7.5 0a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM2 20c0-3.3 3.1-6 7-6s7 2.7 7 6v1H2v-1Zm15.5-4.6c2.6.5 4.5 2.4 4.5 4.6v1h-4v-1c0-1.7-.6-3.3-1.6-4.5l1.1-.1Z',
  analytics: 'M4 20V10h4v10H4Zm6 0V4h4v16h-4Zm6 0v-7h4v7h-4Z',
  // A funnel: applications in at the top, offers out at the bottom.
  pipeline: 'M3 4h18l-7 8.5V21l-4-2.5v-6L3 4Z',
  billing:
    'M3 8a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v1H3V8Zm0 3h18v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-5Zm3 4h4v-1.5H6V15Z',
  integrations:
    'M9 3v4H7a3 3 0 0 0-3 3v3h3.5a2.5 2.5 0 0 1 0 5H4v1a3 3 0 0 0 3 3h3v-4a2.5 2.5 0 0 1 5 0v4h3a3 3 0 0 0 3-3v-3.5a2.5 2.5 0 0 1-5 0V13h4v-3a3 3 0 0 0-3-3h-2V3H9Z',
  messages: 'M4 4h16a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H9l-5 4v-4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z',
  audit:
    'M6 2h8l4 4v16H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2Zm7 1.5V7h3.5L13 3.5ZM8 12h8v1.5H8V12Zm0 4h8v1.5H8V16Zm0-8h4v1.5H8V8Z',

  // -- metrics -------------------------------------------------------------
  student:
    'M12 3 1 9l11 6 9-4.9V17h2V9L12 3ZM5 13.2V17c0 1.7 3.1 3 7 3s7-1.3 7-3v-3.8l-7 3.8-7-3.8Z',
  company:
    'M3 21V6a1 1 0 0 1 1-1h7a1 1 0 0 1 1 1v3h8a1 1 0 0 1 1 1v11H3Zm3-3h2v-2H6v2Zm0-4h2v-2H6v2Zm0-4h2V8H6v2Zm4 8h2v-2h-2v2Zm0-4h2v-2h-2v2Zm0-4h2V8h-2v2Zm5 8h2v-2h-2v2Zm0-4h2v-2h-2v2Zm4 4h2v-2h-2v2Zm0-4h2v-2h-2v2Z',
  application: 'M5 3h9l5 5v13H5V3Zm8 1.5V9h4.5L13 4.5ZM8 12h8v1.5H8V12Zm0 4h6v1.5H8V16Z',
  suspended:
    'M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20ZM4.5 12a7.5 7.5 0 0 1 11.9-6.1L5.9 16.4A7.4 7.4 0 0 1 4.5 12Zm7.5 7.5c-1.6 0-3.1-.5-4.4-1.4L18.1 7.6A7.5 7.5 0 0 1 12 19.5Z',
  signup:
    'M15 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm-9-1V8H4v3H1v2h3v3h2v-3h3v-2H6Zm9 3c-3.9 0-7 2.7-7 6v1h14v-1c0-3.3-3.1-6-7-6Z',
  clock: 'M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm1 10.4V6h-2v7.4l4.7 2.8 1-1.7-3.7-2.1Z',

  // -- row actions ---------------------------------------------------------
  // Solid glyphs, because they sit at 17px inside a 32px button and an
  // outline at that size reads as a smudge.
  check: 'M9.5 16.2 5.3 12l-1.4 1.4L9.5 19 20.6 7.9l-1.4-1.4L9.5 16.2Z',
  star: 'm12 17.3-6.2 3.7 1.6-7L2 9.2l7.1-.6L12 2l2.9 6.6 7.1.6-5.4 4.8 1.6 7L12 17.3Z',
  eye: 'M12 5c-5 0-9 4.5-9 7s4 7 9 7 9-4.5 9-7-4-7-9-7Zm0 11a4 4 0 1 1 0-8 4 4 0 0 1 0 8Zm0-2a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z',
  eyeOff:
    'M2.1 3.5 3.5 2.1l18.4 18.4-1.4 1.4-3.2-3.2A10.5 10.5 0 0 1 12 19c-5 0-9-4.5-9-7 0-1.4 1.2-3.4 3.2-5L2.1 3.5Zm7.3 7.3a2 2 0 0 0 2.8 2.8L9.4 10.8ZM12 5c5 0 9 4.5 9 7 0 1-.7 2.4-1.9 3.7l-3-3A4 4 0 0 0 11.3 8L9.2 5.9C10.1 5.3 11 5 12 5Z',
  pencil:
    'M3 17.2V21h3.8L17.8 10 14 6.2 3 17.2ZM20.7 7.1a1 1 0 0 0 0-1.4l-2.4-2.4a1 1 0 0 0-1.4 0l-1.8 1.8L18.9 8.9l1.8-1.8Z',
  archive: 'M3 4h18v4H3V4Zm1 6h16v10H4V10Zm5 3v2h6v-2H9Z',

  // -- reference data ------------------------------------------------------
  // Four adjacent menu entries, so four distinguishable shapes: sharing one
  // glyph between them would make the icon column noise rather than an aid.
  university: 'M12 3 1 8l11 5 11-5-11-5Zm-7 8.4V16c0 1.7 3.1 3 7 3s7-1.3 7-3v-4.6l-7 3.2-7-3.2Z',
  industry: 'M2 21V11l5 3V11l5 3V11l5 3V4h5v17H2Zm3-5v2h3v-2H5Zm5 0v2h3v-2h-3Zm5 0v2h3v-2h-3Z',
  category: 'M2 5h7l2 2h11v12H2V5Zm2 5v7h16v-7H4Z',
  skill: 'M11 2H2v9l11 11 9-9L11 2Zm-4.5 6a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3Z',
  restore: 'M12 5V1L7 6l5 5V7a6 6 0 1 1-6 6H4a8 8 0 1 0 8-8Z',
  power:
    'M11 3h2v10h-2V3Zm-4.2 3.3 1.4 1.4A6 6 0 1 0 18 12a6 6 0 0 0-1.8-4.3l1.4-1.4A8 8 0 1 1 6.8 6.3Z',

  // -- affordances ---------------------------------------------------------
  chevron: 'm9 6 6 6-6 6',
  menu: 'M4 7h16M4 12h16M4 17h16',
  close: 'M6 6l12 12M18 6 6 18',
  arrowUp: 'm12 5 7 7h-4v7h-6v-7H5l7-7Z',
  external: 'M14 3h7v7h-2V6.4l-8.3 8.3-1.4-1.4L17.6 5H14V3ZM5 5h5v2H6v11h11v-4h2v6H5V5Z',
  arrowRight: 'M5 12h13m-5-6 6 6-6 6',
  // Three dots in a row: the "More" menu on a card's footer.
  more: 'M5 10.25a1.75 1.75 0 1 1 0 3.5 1.75 1.75 0 0 1 0-3.5Zm7 0a1.75 1.75 0 1 1 0 3.5 1.75 1.75 0 0 1 0-3.5Zm7 0a1.75 1.75 0 1 1 0 3.5 1.75 1.75 0 0 1 0-3.5Z',
  // -- feedback and controls (design system) -------------------------------
  info: 'M12 3.75a8.25 8.25 0 1 0 0 16.5 8.25 8.25 0 0 0 0-16.5ZM12 11v5m0-8.25v.5',
  alert: 'M12 4.25 3.5 19h17L12 4.25ZM12 10v4.25m0 2.5v.25',
  filter: 'M4 6h16M7 12h10m-7 6h4',
  reset: 'M4.75 12a7.25 7.25 0 1 0 2.1-5.1M4.75 4.5v3.25H8',
  // A sidebar beside a pane: the collapse / expand control for the rail.
  panelLeft:
    'M4.75 5.75a1 1 0 0 1 1-1h12.5a1 1 0 0 1 1 1v12.5a1 1 0 0 1-1 1H5.75a1 1 0 0 1-1-1V5.75ZM9.5 4.75v14.5',
  sort: 'M8 5v14m0 0-3-3m3 3 3-3M16 19V5m0 0-3 3m3-3 3 3',
  merge: 'M6 4v4.5a5.5 5.5 0 0 0 5.5 5.5H19m0 0-3-3m3 3-3 3M6 20v-5',
  download: 'M12 4v11m0 0-4-4m4 4 4-4M5 19.25h14',
  plus: 'M12 5v14M5 12h14',
  dashboard: 'M4.5 4.5h6v7h-6zM13.5 4.5h6v4h-6zM13.5 11.5h6v8h-6zM4.5 14.5h6v5h-6z',
  briefcase:
    'M4 8.5A1.5 1.5 0 0 1 5.5 7h13A1.5 1.5 0 0 1 20 8.5v9a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 17.5v-9ZM9 7V5.5A1.5 1.5 0 0 1 10.5 4h3A1.5 1.5 0 0 1 15 5.5V7M4 12.5h16',
  clipboard:
    'M9 3.5h6a1 1 0 0 1 1 1V6H8V4.5a1 1 0 0 1 1-1ZM8 4.75H6.5A1.5 1.5 0 0 0 5 6.25v13.25A1.5 1.5 0 0 0 6.5 21h11a1.5 1.5 0 0 0 1.5-1.5V6.25a1.5 1.5 0 0 0-1.5-1.5H16M9 13.5l2 2 4-4.5',
  calendarClock:
    'M19 11V7.5A1.5 1.5 0 0 0 17.5 6h-11A1.5 1.5 0 0 0 5 7.5v11A1.5 1.5 0 0 0 6.5 20H11M5 10h14M9 4v4M15 4v4M17 14.75v2.25l1.5 1M21 17a4 4 0 1 1-8 0 4 4 0 0 1 8 0Z',
  contract:
    'M14 3.5H7A1.5 1.5 0 0 0 5.5 5v14A1.5 1.5 0 0 0 7 20.5h10a1.5 1.5 0 0 0 1.5-1.5V8L14 3.5ZM14 3.5V8h4.5M8.5 12h7M8.5 17c1-1.5 1.9-2 2.4-1s1.4.6 2-.4 1.6-.9 2.6.4',
  video:
    'M4 7.5A1.5 1.5 0 0 1 5.5 6h9A1.5 1.5 0 0 1 16 7.5v9a1.5 1.5 0 0 1-1.5 1.5h-9A1.5 1.5 0 0 1 4 16.5v-9ZM16 10.5 20.5 8v8L16 13.5',
  chat: 'M5 5h14a1.5 1.5 0 0 1 1.5 1.5v9A1.5 1.5 0 0 1 19 17h-8l-4.5 3.5V17H5a1.5 1.5 0 0 1-1.5-1.5v-9A1.5 1.5 0 0 1 5 5ZM8 9.5h8M8 12.75h5',
  userSearch:
    'M10 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7ZM3.5 19.5a6.5 6.5 0 0 1 8.5-6.2M17.25 18.5a2.75 2.75 0 1 0 0-5.5 2.75 2.75 0 0 0 0 5.5ZM19.25 18.25 21 20',
  usersStar:
    'M9 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7ZM2.5 19.5a6.5 6.5 0 0 1 11-4.7M17.5 13l1.05 2.15 2.35.35-1.7 1.65.4 2.35-2.1-1.1-2.1 1.1.4-2.35-1.7-1.65 2.35-.35L17.5 13Z',
  chartBar: 'M4 20h16M7 16.5v-5M12 16.5V6M17 16.5V10',
  building:
    'M5 20.5V5a1.5 1.5 0 0 1 1.5-1.5h7A1.5 1.5 0 0 1 15 5v15.5M15 9.5h2.5A1.5 1.5 0 0 1 19 11v9.5M3.5 20.5h17M8.5 7.5h3M8.5 11h3M8.5 14.5h3',
  team: 'M9 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7ZM2.5 19.5a6.5 6.5 0 0 1 13 0M16 4.3a3.5 3.5 0 0 1 0 6.4M18.5 14a6.5 6.5 0 0 1 3 5.5',
  shieldCheck: 'M12 3.5 5 6v5.5c0 4.2 2.9 7.6 7 9 4.1-1.4 7-4.8 7-9V6l-7-2.5ZM9 12l2 2 4-4.25',
  plug: 'M9 3.5v4M15 3.5v4M6.5 7.5h11v3a5.5 5.5 0 0 1-11 0v-3ZM12 16v4.5',
  creditCard:
    'M4.5 6h15A1.5 1.5 0 0 1 21 7.5v9a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 16.5v-9A1.5 1.5 0 0 1 4.5 6ZM3 10h18M7 14.5h3.5',
  sparkles:
    'M11 3.5l1.7 4.3 4.3 1.7-4.3 1.7L11 15.5l-1.7-4.3L5 9.5l4.3-1.7L11 3.5ZM18 14.5l.85 2.15L21 17.5l-2.15.85L18 20.5l-.85-2.15L15 17.5l2.15-.85L18 14.5Z',
  route:
    'M6 20.5a2 2 0 1 0 0-4 2 2 0 0 0 0 4ZM18 7.5a2 2 0 1 0 0-4 2 2 0 0 0 0 4ZM8 18.5h7.5a3 3 0 0 0 0-6h-7a3 3 0 0 1 0-6H16',
  trendingUp: 'M3.5 17.5l6-6 4 4 7-7M15 8.5h5.5V14',
  user: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM4.5 20.5a7.5 7.5 0 0 1 15 0',
  userCog:
    'M10 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7ZM3.5 19.5a6.5 6.5 0 0 1 8.3-6.25M17.5 19a1.75 1.75 0 1 0 0-3.5 1.75 1.75 0 0 0 0 3.5ZM17.5 13v1.25M17.5 20.25v1.25M20.5 17.25h-1.25M15.75 17.25H14.5M19.6 15.15l-.9.9M16.3 18.45l-.9.9M19.6 19.35l-.9-.9M16.3 16.05l-.9-.9',
  graduationCap:
    'M12 5 2.5 9.5 12 14l9.5-4.5L12 5ZM6.5 11.5V16c0 1.5 2.5 3 5.5 3s5.5-1.5 5.5-3v-4.5M21.5 9.5v5',
  zap: 'M13 3 5 13.5h6L10 21l8-10.5h-6L13 3Z',
  fileText:
    'M14 3.5H7A1.5 1.5 0 0 0 5.5 5v14A1.5 1.5 0 0 0 7 20.5h10a1.5 1.5 0 0 0 1.5-1.5V8L14 3.5ZM14 3.5V8h4.5M9 12.5h6M9 16h6M9 9h2',
  userCheck:
    'M9.5 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7ZM3 19.5a6.5 6.5 0 0 1 11-4.7M15.5 18l2 2 4-4.5',
  flag: 'M5.5 21V4.5M5.5 4.5h12l-2.25 4 2.25 4h-12',
  mapPin:
    'M12 20.75s-6.25-5.4-6.25-10.5a6.25 6.25 0 0 1 12.5 0c0 5.1-6.25 10.5-6.25 10.5ZM12 12.5a2.25 2.25 0 1 0 0-4.5 2.25 2.25 0 0 0 0 4.5Z',
  idBadge:
    'M6.5 3.5h11A1.5 1.5 0 0 1 19 5v14.5a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 5 19.5V5a1.5 1.5 0 0 1 1.5-1.5ZM10 3.5h4M12 12.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5ZM8.5 17.5a3.5 3.5 0 0 1 7 0',
  history: 'M4 12a8 8 0 1 0 2.35-5.65M4 4.5V8h3.5M12 8v4.5l3 1.75',
  package: 'M12 3.5l8 4v9l-8 4-8-4v-9l8-4ZM4 7.5l8 4 8-4M12 11.5v9M8 5.5l8 4',
  repeat:
    'M17 3.5l3 3-3 3M4 11.5V10a3.5 3.5 0 0 1 3.5-3.5H20M7 20.5l-3-3 3-3M20 12.5V14a3.5 3.5 0 0 1-3.5 3.5H4',
  ticket:
    'M4.5 6.5h15A1.5 1.5 0 0 1 21 8v2.25a1.75 1.75 0 0 0 0 3.5V16a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 16v-2.25a1.75 1.75 0 0 0 0-3.5V8a1.5 1.5 0 0 1 1.5-1.5ZM14.5 6.5v2M14.5 11v2M14.5 15.5v2',
  banknote:
    'M4 6.5h16A1.5 1.5 0 0 1 21.5 8v8a1.5 1.5 0 0 1-1.5 1.5H4A1.5 1.5 0 0 1 2.5 16V8A1.5 1.5 0 0 1 4 6.5ZM12 14.75a2.75 2.75 0 1 0 0-5.5 2.75 2.75 0 0 0 0 5.5ZM6 10v4M18 10v4',
  landmark:
    'M3.5 20.5h17M5 17.5h14M6.5 17.5v-7M10 17.5v-7M14 17.5v-7M17.5 17.5v-7M12 3.5l8.5 5h-17l8.5-5Z',
  factory: 'M3.5 20.5V11l5 3v-3l5 3V8l7 3v9.5h-17ZM7.5 17.5h1M11.5 17.5h1M15.5 17.5h1',
  tag: 'M3.5 12.2V4.5a1 1 0 0 1 1-1h7.7a1 1 0 0 1 .7.3l8.3 8.3a1 1 0 0 1 0 1.4l-7.7 7.7a1 1 0 0 1-1.4 0l-8.3-8.3a1 1 0 0 1-.3-.7ZM8 8h.01',
  funnel: 'M3.5 5h17l-6.5 7.5V19l-4 1.5v-8L3.5 5Z',
  panelLeftClose:
    'M4.75 5.75a1 1 0 0 1 1-1h12.5a1 1 0 0 1 1 1v12.5a1 1 0 0 1-1 1H5.75a1 1 0 0 1-1-1V5.75ZM9.5 4.75v14.5M15.5 10l-2 2 2 2',
  panelLeftOpen:
    'M4.75 5.75a1 1 0 0 1 1-1h12.5a1 1 0 0 1 1 1v12.5a1 1 0 0 1-1 1H5.75a1 1 0 0 1-1-1V5.75ZM9.5 4.75v14.5M13.5 10l2 2-2 2',
  attach:
    'M19.5 11.5 12 19a4.75 4.75 0 0 1-6.7-6.7l7.8-7.8a3.2 3.2 0 0 1 4.5 4.5l-7.8 7.8a1.6 1.6 0 0 1-2.3-2.3l7-7',
  mail: 'M4.5 6h15a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1h-15a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1Zm-.5 1 8 6 8-6',
  phone:
    'M6.6 3.75h2.6l1.3 3.9-1.95 1.3a10.4 10.4 0 0 0 6.5 6.5l1.3-1.95 3.9 1.3v2.6a1.3 1.3 0 0 1-1.3 1.3A15.6 15.6 0 0 1 5.3 5.05a1.3 1.3 0 0 1 1.3-1.3Z',
  send: 'M20.25 3.75 10.5 13.5M20.25 3.75l-6 16.5-3.75-6.75-6.75-3.75 16.5-6Z',
  upload: 'M12 15.5v-11m0 0-4 4m4-4 4 4M5 19.25h14',
  trash:
    'M5 7h14M10 4.75h4M7 7l.8 11.3a1 1 0 0 0 1 .95h6.4a1 1 0 0 0 1-.95L17 7M10.25 10.5v5.25m3.5-5.25v5.25',

  // -- social ---------------------------------------------------------------
  // Brand marks, so unlike the glyphs above these are traced shapes rather
  // than drawings on the 24x24 stroke grid -- they are not ours to restyle.
  x: 'M18.9 2H22l-7.5 8.6L23.3 22h-6.9l-5.4-7-6.2 7H1.7l8-9.2L1 2h7.1l4.9 6.4L18.9 2Zm-1.2 18h1.9L7.4 3.9H5.4l12.3 16.1Z',
  linkedin:
    'M4.98 3.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5ZM3 9h4v12H3V9Zm6 0h3.8v1.7h.05c.53-.95 1.83-1.95 3.76-1.95 4.02 0 4.76 2.5 4.76 5.76V21h-4v-5.6c0-1.34-.02-3.06-1.9-3.06-1.9 0-2.19 1.45-2.19 2.96V21H9V9Z',
  github:
    'M12 .3a12 12 0 0 0-3.8 23.38c.6.11.82-.26.82-.58v-2.03c-3.34.72-4.04-1.61-4.04-1.61-.55-1.39-1.34-1.76-1.34-1.76-1.09-.74.08-.73.08-.73 1.2.08 1.84 1.24 1.84 1.24 1.07 1.83 2.81 1.3 3.5 1 .1-.78.42-1.3.76-1.6-2.67-.3-5.47-1.33-5.47-5.93 0-1.31.47-2.38 1.24-3.22-.14-.3-.54-1.52.1-3.18 0 0 1-.32 3.3 1.23a11.5 11.5 0 0 1 6 0c2.28-1.55 3.29-1.23 3.29-1.23.64 1.66.24 2.88.12 3.18a4.65 4.65 0 0 1 1.23 3.22c0 4.61-2.8 5.62-5.48 5.92.43.37.82 1.1.82 2.22v3.29c0 .32.21.7.82.58A12 12 0 0 0 12 .3Z',
  instagram:
    'M12 2c-2.72 0-3.06.01-4.12.06-1.07.05-1.8.22-2.43.46a4.9 4.9 0 0 0-1.77 1.16A4.9 4.9 0 0 0 2.52 5.4c-.24.63-.41 1.36-.46 2.43C2.01 8.9 2 9.24 2 12s.01 3.1.06 4.17c.05 1.07.22 1.8.46 2.43a4.9 4.9 0 0 0 1.16 1.77 4.9 4.9 0 0 0 1.77 1.16c.63.24 1.36.41 2.43.46 1.06.05 1.4.06 4.12.06s3.06-.01 4.12-.06c1.07-.05 1.8-.22 2.43-.46a5.1 5.1 0 0 0 2.93-2.93c.24-.63.41-1.36.46-2.43.05-1.06.06-1.4.06-4.17s-.01-3.1-.06-4.17c-.05-1.07-.22-1.8-.46-2.43a4.9 4.9 0 0 0-1.16-1.77 4.9 4.9 0 0 0-1.77-1.16c-.63-.24-1.36-.41-2.43-.46C15.06 2.01 14.72 2 12 2Zm0 1.8c2.67 0 2.99.01 4.04.06.97.04 1.5.21 1.86.35.46.18.8.4 1.15.74.34.35.56.69.74 1.15.14.36.31.89.35 1.86.05 1.05.06 1.37.06 4.04s-.01 2.99-.06 4.04c-.4.97-.21 1.5-.35 1.86-.18.46-.4.8-.74 1.15-.35.34-.69.56-1.15.74-.36.14-.89.31-1.86.35-1.05.05-1.37.06-4.04.06s-2.99-.01-4.04-.06c-.97-.04-1.5-.21-1.86-.35a3.1 3.1 0 0 1-1.15-.74 3.1 3.1 0 0 1-.74-1.15c-.14-.36-.31-.89-.35-1.86-.05-1.05-.06-1.37-.06-4.04s.01-2.99.06-4.04c.04-.97.21-1.5.35-1.86.18-.46.4-.8.74-1.15.35-.34.69-.56 1.15-.74.36-.14.89-.31 1.86-.35 1.05-.05 1.37-.06 4.04-.06Zm0 3.06a5.14 5.14 0 1 0 0 10.28 5.14 5.14 0 0 0 0-10.28Zm0 8.48a3.34 3.34 0 1 1 0-6.68 3.34 3.34 0 0 1 0 6.68Zm6.54-8.68a1.2 1.2 0 1 1-2.4 0 1.2 1.2 0 0 1 2.4 0Z',
  facebook:
    'M22 12a10 10 0 1 0-11.56 9.88v-6.99H7.9V12h2.54V9.8c0-2.51 1.5-3.89 3.78-3.89 1.09 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56V12h2.78l-.45 2.89h-2.33v6.99A10 10 0 0 0 22 12Z',
};

/** Glyphs that read as outlines rather than solids. */
const STROKED = new Set([
  'chevron',
  'menu',
  'close',
  'messages',
  'arrowRight',
  // The shell glyphs are outline drawings -- a filled ring cannot show a
  // hole, so `target`, `calendar` and the rest have to be stroked.
  'home',
  'search',
  'bookmark',
  'calendar',
  'document',
  'target',
  'settings',
  'logout',
  'bell',
  'clockOutline',
  'info',
  'alert',
  'filter',
  'reset',
  'panelLeft',
  'sort',
  'merge',
  'download',
  'plus',
  'dashboard',
  'briefcase',
  'clipboard',
  'calendarClock',
  'contract',
  'chat',
  'video',
  'userSearch',
  'usersStar',
  'chartBar',
  'building',
  'team',
  'shieldCheck',
  'plug',
  'creditCard',
  'sparkles',
  'route',
  'trendingUp',
  'user',
  'userCog',
  'graduationCap',
  'zap',
  'fileText',
  'userCheck',
  'flag',
  'mapPin',
  'idBadge',
  'history',
  'package',
  'repeat',
  'ticket',
  'banknote',
  'landmark',
  'factory',
  'tag',
  'funnel',
  'panelLeftClose',
  'panelLeftOpen',
  'attach',
  'send',
  'upload',
  'trash',
]);

/**
 * `filled` paints the inside of an outline glyph too -- the "on" state of a
 * toggle (a saved bookmark, a starred candidate), so on and off differ in
 * shape and not only in colour.
 */
export function Icon({ name, className, size = 20, filled = false, strokeWidth = 1.75 }) {
  const path = PATHS[name];
  if (!path) return null;

  const stroked = STROKED.has(name);

  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      className={cn('shrink-0', className)}
      aria-hidden="true"
      focusable="false"
      fill={stroked && !filled ? 'none' : 'currentColor'}
      stroke={stroked ? 'currentColor' : 'none'}
      strokeWidth={stroked ? strokeWidth : undefined}
      strokeLinecap={stroked ? 'round' : undefined}
      strokeLinejoin={stroked ? 'round' : undefined}
    >
      <path d={path} />
    </svg>
  );
}
